import { useQuery } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";

import CaseStorageAPI from "@/api/storage/index.js";

import type { GetStorageQuery } from "./Tables/types";
import type { StorageCocRecord } from "./storage.types";

/**
 * Query-key factory for the mfui-based storage queries (StorageTable,
 * StorageFlyout). Mirrors `evidenceQueryKeys` in evidence.query.ts.
 *
 * The legacy string namespace `"storage:list"` is still used by the
 * case-route StorageSection, the storage selectors, and the details page.
 * Refreshers that need to reach every storage list must invalidate both
 * `legacyLists` and `infiniteLists` until those pages are cut over.
 */
export const storageQueryKeys = {
  all: ["storage"] as QueryKey,
  infiniteLists: ["storage", "infinite-list"] as QueryKey,
  infiniteList: (query: GetStorageQuery): QueryKey => [
    "storage",
    "infinite-list",
    query,
  ],
  item: (uuid?: string): QueryKey => ["storage", "item", { uuid }],
  legacyLists: ["storage:list"] as QueryKey,
  // Case-route StorageSection scopes its list under a separate legacy
  // namespace so case-tab pages don't share cache entries with /storage.
  legacyCaseLists: ["cases:storage:list"] as QueryKey,
  // Reference data for the storage forms. Custom fields are NOT keyed here —
  // use CustomFieldsQuery.useGetCustomFields so forms share the cache with
  // the table's custom-field columns.
  types: ["storage", "types", "list"] as QueryKey,
  providers: ["storage", "providers", "list"] as QueryKey,
  models: ["storage", "models", "list"] as QueryKey,
  modelNumbers: ["storage", "model-numbers", "list"] as QueryKey,
  // Blanket prefix for every CoC entry regardless of item. Use this to
  // invalidate after mutations that write storage_coc rows for many items
  // (bulk move/release/destroy). Note cocRecords() with no arg does NOT
  // work for that: it produces { storage_id: undefined }, which react-query
  // partial matching never matches against a real id.
  cocRecordsAll: ["storage:cocData"] as QueryKey,
  // Chain-of-custody records for one item, all assignment scopes. Use this
  // prefix for refetch/invalidate — partial matching reaches every scoped
  // entry below it.
  cocRecords: (storageId?: number): QueryKey => [
    "storage:cocData",
    { storage_id: storageId },
  ],
  // Full observer key: the API returns a different record set per
  // case_assignment_id (assignment-scoped vs the IS NULL unassigned-lifecycle
  // branch), so the scope must be part of the key or observers with
  // different scopes would share one cache entry. undefined and null both
  // mean "unassigned" and normalize to null so they share the entry.
  cocRecordsScoped: (
    storageId?: number,
    caseAssignmentId?: number | null
  ): QueryKey => [
    "storage:cocData",
    { storage_id: storageId },
    { case_assignment_id: caseAssignmentId ?? null },
  ],
};

/**
 * Chain-of-custody records for one storage item. Shared by the CoC tab
 * (CocContent) and the Current Location hover tooltip
 * (StorageCurrentLocationCell), mirroring CocQuery.useGetCocRecords on the
 * evidence side (ENG-709).
 *
 * The key keeps the legacy `"storage:cocData"` namespace that CocContent's
 * refetch sites target (via storageQueryKeys.cocRecords), with the
 * assignment scope appended — see cocRecordsScoped for why.
 *
 * Caching behavior (`staleTime`, `refetchOnWindowFocus`) is chosen by each
 * call site: the tooltip caches for 5 minutes; the CoC tab keeps the default
 * refetch-on-mount behavior.
 */
export function useGetStorageCocRecords(
  storageId: number | undefined,
  caseAssignmentId: number | null | undefined,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    retry?: boolean | number;
  }
) {
  // Spread only the keys the caller actually passed. TanStack merges options
  // with object spread, so an explicit `refetchOnWindowFocus: undefined` would
  // override the QueryClient defaultOptions instead of falling through to them.
  const { enabled, ...passthrough } = options ?? {};

  return useQuery<StorageCocRecord[]>({
    queryKey: storageQueryKeys.cocRecordsScoped(storageId, caseAssignmentId),
    queryFn: async () => {
      const records = await CaseStorageAPI.getCocRecords({
        storage_id: storageId,
        case_assignment_id: caseAssignmentId,
      });
      // connection.js resolves API failures as { success: false, error }
      // instead of rejecting; without this guard that object would be cached
      // as data and crash consumers expecting an array.
      if (!Array.isArray(records)) {
        throw new Error(
          `Failed to fetch chain of custody records for storage item ${storageId}`
        );
      }
      return records as StorageCocRecord[];
    },
    enabled: storageId != null && (enabled ?? true),
    // Failed requests already toast via the connection.js interceptor, once
    // per attempt — don't retry unless a caller opts in.
    retry: false,
    ...passthrough,
  });
}
